import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Button,
  Paper,
  Alert
} from "@mui/material";
import { CheckCircle, Error, HourglassEmpty } from "@mui/icons-material";
import API from "util/API";

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(4),
    textAlign: 'center',
    maxWidth: 500,
    width: '100%',
  },
  icon: {
    fontSize: 64,
    marginBottom: theme.spacing(2),
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  pendingIcon: {
    color: theme.palette.warning.main,
  },
  button: {
    marginTop: theme.spacing(2),
  },
}));

const WechatPaymentResult = () => {
  const classes = useStyles();
  const { restaurantId, tableId, orderNumber, orderNo } = useParams();
  const history = useHistory();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [error, setError] = useState(null);

  useEffect(() => {
    checkPaymentStatus();
  }, [restaurantId, orderNo]);

  const checkPaymentStatus = async () => {
    try {
      setPaymentStatus('checking');
      const response = await API.get(
        `/wechatPaymentStatus?restaurantId=${restaurantId}&orderNo=${orderNo}`
      );
      
      if (response.data && response.data.status) {
        setPaymentStatus(response.data.status);
      } else {
        setPaymentStatus('unknown');
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError('Failed to check payment status');
      setPaymentStatus('error');
    }
  };

  const handleRetry = () => {
    checkPaymentStatus();
  };

  const handleGoBack = () => {
    history.goBack();
  };

  const handleGoToOrder = () => {
    history.push(`/orderSuccess/${orderNumber}`);
  };

  const renderContent = () => {
    switch (paymentStatus) {
      case 'checking':
        return (
          <>
            <CircularProgress className={classes.icon} />
            <Typography variant="h5" gutterBottom>
              Checking Payment Status
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Please wait while we verify your payment...
            </Typography>
          </>
        );

      case 'success':
      case 'paid':
        return (
          <>
            <CheckCircle className={`${classes.icon} ${classes.successIcon}`} />
            <Typography variant="h5" gutterBottom>
              Payment Successful!
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Your WeChat payment has been processed successfully.
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Order Number: {orderNumber}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={handleGoToOrder}
            >
              View Order
            </Button>
          </>
        );

      case 'pending':
        return (
          <>
            <HourglassEmpty className={`${classes.icon} ${classes.pendingIcon}`} />
            <Typography variant="h5" gutterBottom>
              Payment Pending
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Your payment is being processed. This may take a few minutes.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              className={classes.button}
              onClick={handleRetry}
            >
              Check Again
            </Button>
          </>
        );

      case 'failed':
      case 'error':
        return (
          <>
            <Error className={`${classes.icon} ${classes.errorIcon}`} />
            <Typography variant="h5" gutterBottom>
              Payment Failed
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              {error || 'Your payment could not be processed. Please try again.'}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={handleRetry}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              className={classes.button}
              onClick={handleGoBack}
              style={{ marginLeft: 8 }}
            >
              Go Back
            </Button>
          </>
        );

      default:
        return (
          <>
            <Error className={`${classes.icon} ${classes.errorIcon}`} />
            <Typography variant="h5" gutterBottom>
              Unknown Status
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Unable to determine payment status. Please try again.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={handleRetry}
            >
              Check Again
            </Button>
          </>
        );
    }
  };

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        {renderContent()}
      </Paper>
    </Box>
  );
};

export default WechatPaymentResult;
